import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
import cn from "classnames";
import { Title3 } from "@components/new/Typo";
import { Table, TableCellLink, TableColumn, TableRowType } from "@components/new/Table";
import { MessageModalContent } from '@components/modals/MessageModalNew';
import { Loader } from "@components/new/Loader";
import { Marketplace } from "@components/new/Marketplace";
import Photo from "@components/new/Photo";
import {
  isYmItem,
  isWbItem,
  isAliItem,
  TYmItemSearch,
  TWbItemSearch,
  TAliItemSearch,
} from "@api/apiClient/fbp";
import NoItemsIcon from "@assets/icons/hugeicons/package-remove.svg?react";
import StoreIcon from "@assets/icons/outline/store.svg?react";
import styles from './Form.module.scss';

export interface FormType {
  wbId: string;
  wbSellerId: string;
  ymSellerId: string;
}

type TRowDefault = (TYmItemSearch | TWbItemSearch | TAliItemSearch);

export const SearchMenuComponent = <TRow extends TYmItemSearch | TWbItemSearch | TAliItemSearch = TRowDefault>({ items, onClickItem, isLoading, isWb, isShort, showMarketplace }: {
  items: TRow[];
  onClickItem: (v: TRow[]) => void;
  isLoading?: boolean;
  isWb?: boolean,
  isShort?: boolean;
  showMarketplace?: boolean,
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();

  const searchItemsColumns: TableColumn<TRow>[] = useMemo(() => [
    {
      header: t("fbpPage.matchings.columnFoto"),
      renderCell: (row: TableRowType<TRow>) => (
        <Photo src={row.data.primaryImage} />
      ),
      width: "80px",
      fixWidth: true,
    },
    ...(showMarketplace ? [{
      header: t("fbpPage.matchings.columnMarketplace"),
      renderCell: (row: TableRowType<TRow>) => (
        <Marketplace name={row.data.marketplace} size="small" />
      ),
      alignment: "center" as "center",
      width: "40px",
      fixWidth: true,
    }] : []),
    {
      header: t("fbpPage.matchings.columnName"),
      renderCell: (row: TableRowType<TRow>) => {
        const description = isYmItem(row.data) ? row.data.sku : (
          isWbItem(row.data) ? row.data.wbNmId : (
            isAliItem(row.data) ? row.data.skuCode : ""
          )
        );
        return (
          <TableCellLink
            title={row.data.name}
            description={description}
            thirdLine={row.data.sellerName ? (
              <span className={styles.storeLine}><StoreIcon /> {row.data.sellerName}</span>
            ) : undefined}
            to={row.data.url}
            external
            maxWidth="680px"
          />
        )
      },
      width: "680px",
    },
    ...(isWb ? [{
      header: t("fbpPage.matchings.columnSize"),
      valueGetter: (row: TRow) => ((isWbItem(row) && row.wbTechSize) || ""),
      width: "50px",
      fixWidth: true,
      // alignment: "center",
    }] : []),
  ], [currentLanguage, items, isWb]);

  if (isLoading) {
    return (
      <div className={cn(styles.searchMenuEmpty, isShort && styles.searchMenuShort)}>
        <Loader className={styles.loader} />
      </div>
    )
  }

  if (!items.length) {
    return (
      <div className={cn(styles.searchMenuEmpty, isShort && styles.searchMenuShort)}>
        <MessageModalContent
          title={t('fbpPage.createOrder.searchDropdown.noItemsTitle')}
          description={t('fbpPage.createOrder.searchDropdown.noItemsDescription')}
          variant="info"
          Icon={<NoItemsIcon />}
          iconSize="l"
        />
      </div>
    )
  }

  return (
    <div className={cn(styles.searchMenu, isShort && styles.searchMenuShort)}>
      <div className={styles.searchMenuHeader}>
        <Title3 color="grey-800">{t('fbpPage.matchings.someItems')}</Title3>
      </div>
      <Table<TRow>
        columns={searchItemsColumns}
        data={items}
        rowClassName={(row) => (row.data.matchingLinkId ? styles.forbiddenRow : "")}
        rowTooltip={(row) => (row.matchingLinkId ? t('fbpPage.matchings.alreadyMatchedTooltip') : "")}
        rowOnClick={(row) => {
          if (row.matchingLinkId) return;
          onClickItem([row]);
        }}
      />
    </div>
  )
};

export const SearchMenuComponentWb = (props: {
  items: (TYmItemSearch | TAliItemSearch | TWbItemSearch)[];
  onClickItem: (v: (TYmItemSearch | TAliItemSearch | TWbItemSearch)[]) => void;
  isLoading?: boolean;
}) => (
  <SearchMenuComponent {...props} isWb />
)

// компонент для поиска товаров по артикулу, вариант с выводом маркетплейса
export const SearchMenuComponentMP = (props: {
  items: (TYmItemSearch | TAliItemSearch | TWbItemSearch)[];
  onClickItem: (v: (TYmItemSearch | TAliItemSearch | TWbItemSearch)[]) => void;
  isLoading?: boolean;
}) => (
  <SearchMenuComponent {...props} isWb showMarketplace />
)

// вариант с выводом маркетплейса и уменьшенной высотой
export const SearchMenuComponentMPShort = (props: {
  items: (TYmItemSearch | TAliItemSearch | TWbItemSearch)[];
  onClickItem: (v: (TYmItemSearch | TAliItemSearch | TWbItemSearch)[]) => void;
  isLoading?: boolean;
}) => (
  <SearchMenuComponent {...props} isWb showMarketplace isShort />
)
